/**
 * Allowed ListSubscriptions status filters (billing-manager/pkg/server/common.go).
 * Stripe uses American spelling: "canceled", not "cancelled".
 */
export const BILLING_SUBSCRIPTION_STATUSES = Object.freeze({
  active: "active",
  trialing: "trialing",
  canceled: "canceled",
  past_due: "past_due",
});

const ALLOWED = new Set(Object.values(BILLING_SUBSCRIPTION_STATUSES));

/** Default list used by react-web account billing. */
export const DEFAULT_LIST_SUBSCRIPTION_STATUSES = [
  BILLING_SUBSCRIPTION_STATUSES.active,
  BILLING_SUBSCRIPTION_STATUSES.past_due,
];

/** Includes canceled subs for reactivate on checkout. */
export const CHECKOUT_LIST_SUBSCRIPTION_STATUSES = [
  BILLING_SUBSCRIPTION_STATUSES.active,
  BILLING_SUBSCRIPTION_STATUSES.trialing,
  BILLING_SUBSCRIPTION_STATUSES.past_due,
  BILLING_SUBSCRIPTION_STATUSES.canceled,
];

/** Plan badge / billing overview: subs that count as "holding a plan" (active or trialing). */
export const PLAN_BADGE_LIST_SUBSCRIPTION_STATUSES = [
  BILLING_SUBSCRIPTION_STATUSES.active,
  BILLING_SUBSCRIPTION_STATUSES.trialing,
];

/** Display lists (subscriptions table, renewal, post-checkout poll): active, trialing, overdue. */
export const DISPLAY_LIST_SUBSCRIPTION_STATUSES = [
  BILLING_SUBSCRIPTION_STATUSES.active,
  BILLING_SUBSCRIPTION_STATUSES.trialing,
  BILLING_SUBSCRIPTION_STATUSES.past_due,
];

/**
 * @param {string[]} [statuses]
 * @returns {string[]}
 */
export function normalizeSubscriptionStatuses(statuses) {
  if (!Array.isArray(statuses) || statuses.length === 0) {
    return [...DEFAULT_LIST_SUBSCRIPTION_STATUSES];
  }

  const normalized = [];
  for (const raw of statuses) {
    const s = String(raw ?? "")
      .trim()
      .toLowerCase();
    if (!s) continue;
    const mapped = s === "cancelled" ? BILLING_SUBSCRIPTION_STATUSES.canceled : s;
    if (ALLOWED.has(mapped) && !normalized.includes(mapped)) {
      normalized.push(mapped);
    }
  }

  return normalized.length > 0
    ? normalized
    : [...DEFAULT_LIST_SUBSCRIPTION_STATUSES];
}

export function isCanceledSubscriptionStatus(status) {
  const s = String(status ?? "").toLowerCase();
  return s === "canceled" || s === "cancelled";
}

/** Canonical active statuses for FE filtering (includes past_due + trialing). */
export const ACTIVE_STATUSES = new Set(["active", "trialing", "past_due"]);

/**
 * Lower-cased status string for a subscription, handling proto/JSON variants.
 * @param {object|null|undefined} sub
 */
export function normalizeStatus(sub) {
  if (!sub) return "";
  return String(sub.status ?? sub.Status ?? "").toLowerCase();
}

/**
 * @param {object|null|undefined} sub
 * @returns {boolean}
 */
export function isActiveSubscription(sub) {
  return ACTIVE_STATUSES.has(normalizeStatus(sub));
}

/**
 * @param {Array<object>|null|undefined} subscriptions
 */
export function listActiveSubscriptions(subscriptions = []) {
  if (!Array.isArray(subscriptions)) return [];
  return subscriptions.filter(isActiveSubscription);
}

/** Statuses that grant a copier seat: active or trialing. Excludes past_due. */
export const SEAT_ENTITLED_STATUSES = new Set([
  BILLING_SUBSCRIPTION_STATUSES.active,
  BILLING_SUBSCRIPTION_STATUSES.trialing,
]);

export function isSeatEntitledSubscription(sub) {
  return SEAT_ENTITLED_STATUSES.has(normalizeStatus(sub));
}

export function listSeatEntitledSubscriptions(subscriptions = []) {
  if (!Array.isArray(subscriptions)) return [];
  return subscriptions.filter(isSeatEntitledSubscription);
}

/**
 * Self-serve cancellable: active or trialing, and not already set to end.
 * past_due is excluded, as in checkout-v2.
 * @param {object|null|undefined} sub
 */
export function isCancelableSubscription(sub) {
  return isSeatEntitledSubscription(sub) && !sub?.endsOn?.seconds;
}

/**
 * Still running: cancellable, and not one of `endingIds` — cancels billing has not reported an end date for
 * yet, since the date only arrives with Stripe's webhook.
 * @param {Array<object>|null|undefined} subscriptions
 * @param {string[]} [endingIds]
 */
export function listRunningSubscriptions(subscriptions, endingIds = []) {
  if (!Array.isArray(subscriptions)) return [];
  return subscriptions.filter((sub) => isCancelableSubscription(sub) && !endingIds.includes(sub.id));
}

/**
 * True when cancelling this subscription whole leaves nothing else running: the customer is leaving.
 * A subscription already in its cancellation period does not count as running.
 * @param {Array<object>|null|undefined} subscriptions
 * @param {string|null|undefined} subscriptionId
 * @param {string[]} [endingIds]
 */
export function isLastCancelableSubscription(subscriptions, subscriptionId, endingIds = []) {
  const running = listRunningSubscriptions(subscriptions, endingIds);
  return Boolean(subscriptionId) && running.length === 1 && running[0].id === subscriptionId;
}

/**
 * True iff the subscription is currently inside its Stripe trial window.
 * Used by the checkout router to avoid silently inheriting a sub's trial
 * when attaching a new product line — Stripe scopes trial_end to the
 * whole sub, so any added item would ride the remaining trial days for
 * free without going through `createSubscriptionWithTrial`'s eligibility
 * / history guards.
 *
 * Status-only check: the `SubscriptionLite` proto does not carry
 * `trial_end`, so we trust Stripe's status transition (`trialing` →
 * `active`) propagated through BM. Slight skew is possible if BM hasn't
 * refreshed yet — the worst case is forking when a merge would have
 * sufficed (extra sub, no incorrect billing).
 *
 * @param {object|null|undefined} sub
 * @returns {boolean}
 */
export function isSubInTrial(sub) {
  return normalizeStatus(sub) === BILLING_SUBSCRIPTION_STATUSES.trialing;
}
